import { useState, useEffect } from "react";
import axios from "axios";
import { API, formatApiErrorDetail } from "../App";
import { toast } from "sonner";
import { Send, CheckCircle2 } from "lucide-react";

export default function StudentForm({ formId = "top" }) {
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    axios.get(`${API}/student-form/questions`).then(r => setQuestions(r.data)).catch(() => {});
  }, []);

  const setAnswer = (id, val) => setAnswers(prev => ({ ...prev, [id]: val }));

  const submit = async () => {
    const payload = questions.map(q => ({ question_id: q.id, question: q.label, answer: (answers[q.id] || "").trim() }));
    if (payload.every(a => !a.answer)) { toast.error("Compila il form prima di inviare"); return; }
    const missing = questions.filter(q => !(answers[q.id] || "").trim());
    if (missing.length > 0) { toast.error(`Compila tutte le domande (manca: ${missing[0].label})`); return; }
    setSending(true);
    try {
      await axios.post(`${API}/student-form/submit`, { answers: payload });
      setSent(true);
      toast.success("Candidatura inviata!");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSending(false); }
  };

  if (sent) {
    return (
      <div className="glass-card p-8 text-center" style={{ borderColor: "rgba(47,216,168,0.4)" }} data-testid={`student-form-sent-${formId}`}>
        <CheckCircle2 size={44} className="mx-auto mb-4" style={{ color: "var(--mm-gold)" }} />
        <h3 className="text-xl font-bold mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif", color: "var(--mm-text-primary)" }}>
          Candidatura inviata!
        </h3>
        <p className="text-sm" style={{ color: "var(--mm-text-secondary)" }}>
          Grazie! Le tue risposte sono state ricevute. Ti contatteremo al più presto.
        </p>
      </div>
    );
  }

  if (questions.length === 0) return null;

  return (
    <div className="glass-card p-7 sm:p-10" style={{ borderColor: "rgba(47,216,168,0.25)" }} data-testid={`student-form-${formId}`}>
      <h3 className="text-xl sm:text-2xl font-bold mb-2 gold-text" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
        Candidati come Studente
      </h3>
      <p className="text-sm mb-8" style={{ color: "var(--mm-text-secondary)" }}>
        Compila il form: valuteremo le tue risposte per capire se possiamo collaborare.
      </p>
      <div className="space-y-9">
        {questions.map((q, i) => (
          <div key={q.id} data-testid={`form-question-${formId}-${i}`}>
            <label className="text-sm font-semibold mb-3 block" style={{ color: "var(--mm-gold)", fontFamily: "'Space Grotesk', sans-serif" }}>
              {q.label}
            </label>
            {q.type === "choice" && q.options?.length > 0 ? (
              <select
                value={answers[q.id] || ""}
                onChange={e => setAnswer(q.id, e.target.value)}
                className="w-full px-4 py-3.5 rounded-lg text-sm outline-none focus:border-teal-400 transition-colors cursor-pointer"
                style={{ backgroundColor: "var(--mm-surface)", border: "1px solid var(--mm-border)", color: answers[q.id] ? "var(--mm-text-primary)" : "var(--mm-text-muted)" }}
                data-testid={`form-select-${formId}-${i}`}
              >
                <option value="" disabled>Seleziona una risposta...</option>
                {q.options.map((opt, oi) => (
                  <option key={oi} value={opt}>{opt}</option>
                ))}
              </select>
            ) : (
              <textarea value={answers[q.id] || ""} onChange={e => setAnswer(q.id, e.target.value)}
                rows={q.label.length > 40 ? 4 : 2}
                className="w-full px-4 py-3.5 rounded-lg text-sm outline-none resize-y focus:border-teal-400 transition-colors"
                style={{ backgroundColor: "var(--mm-surface)", border: "1px solid var(--mm-border)", color: "var(--mm-text-primary)" }}
                placeholder="La tua risposta..."
                data-testid={`form-answer-${formId}-${i}`} />
            )}
          </div>
        ))}
      </div>
      <button onClick={submit} disabled={sending}
        className="w-full mt-9 py-4 rounded-lg font-bold uppercase text-sm transition-all duration-300 hover:scale-[1.01] flex items-center justify-center gap-2"
        style={{ fontFamily: "'Space Grotesk', sans-serif", letterSpacing: "0.08em", background: "linear-gradient(135deg, #2FD8A8 0%, #1FB489 100%)", color: "#0A0A0A", opacity: sending ? 0.6 : 1 }}
        data-testid={`form-submit-${formId}`}>
        <Send size={16} /> {sending ? "Invio..." : "Invia candidatura"}
      </button>
    </div>
  );
}
